import { deriveInitialTaskAction } from './deriveInitialTaskAction';
import { draftWizardUrl, getTaskDraft } from './taskDrafts';

/**
 * Single source of truth for navigating to the task landing page.
 *
 * Every usecase row maps to one task bucket (`sub_group_name`) and one active
 * objective id (`_id`). This helper builds the canonical `/task/:taskName`
 * URL with `?task_id=<usecaseId>`.
 *
 * For legacy rows missing `sub_group_name` (databases that pre-date the
 * `20260427_120000_backfill_sub_group_name` migration), we fall back to
 * `usecase_name` so single tasks still resolve to a unique URL instead of
 * sharing an empty bucket with their siblings.
 *
 * @param {Object} usecase - LLMUsecaseInfo row.
 * @param {{ task_id?: string, objective?: string, action?: string }} [opts]
 * Optional query params. `objective` is kept as a legacy alias for `task_id`.
 * @returns {string|null} The URL or `null` when the usecase is incomplete.
 */
export const taskUrlForUsecase = (usecase, { task_id, objective, action } = {}) => {
    const taskName =
        (usecase.sub_group_name && usecase.sub_group_name.trim()) || usecase.usecase_name;
    if (!taskName) return null;
    const resolvedTaskId = task_id || objective || usecase?._id || null;
    const params = new URLSearchParams();
    if (resolvedTaskId) {
        params.set('task_id', resolvedTaskId);
    }
    if (action) {
        params.set('action', action);
    }
    const q = params.toString() ? `?${params.toString()}` : '';
    return `/task/${encodeURIComponent(taskName)}${q}`;
};

/**
 * Single source of truth for *how* to open a task. Every entry point — the
 * Projects card/row and the top-navigation breadcrumb — must go through this:
 *
 * - Draft tasks (creation wizard exited mid-flow) resume `/create-task` at
 *   the saved step, matching a click on the Projects draft card.
 * - Live tasks land on the tab derived from the sub-group (Model
 *   Configuration / Data Editor / Evaluations) instead of the default
 *   Data Overview.
 *
 * @param {Object} targetUsecase - The objective row to open (the active one, or
 *   the bucket's first when entering the task fresh).
 * @param {Array<Object>} [siblingUsecases] - All objectives under the task's
 *   `sub_group_name`; used to derive multi-objective behavior and to find a
 *   draft in the same bucket. Defaults to `[targetUsecase]`.
 * @param {{ evaluationResultsReady?: Object }} [options]
 * @returns {{ url: string, usecaseId: string|null, state: Object|undefined }|null}
 *   Navigation payload, or `null` when the usecase cannot resolve a URL.
 */
export const buildTaskNavigation = (
    targetUsecase,
    siblingUsecases,
    { evaluationResultsReady = {} } = {}
) => {
    if (!targetUsecase) return null;
    const usecases =
        Array.isArray(siblingUsecases) && siblingUsecases.length
            ? siblingUsecases
            : [targetUsecase];

    // Prefer the clicked row, then any sibling in the same task — same rule
    // as the Projects draft card, so the breadcrumb cannot drop a draft into
    // task management.
    const draftUsecase = [targetUsecase, ...usecases].find((uc) => getTaskDraft(uc));
    if (draftUsecase) {
        const draft = getTaskDraft(draftUsecase);
        if (draft) {
            return {
                url: draftWizardUrl(draftUsecase._id, draft.step),
                usecaseId: draftUsecase._id || null,
                state: undefined,
            };
        }
    }

    const isMultiObjective = usecases.length > 1;
    const action = deriveInitialTaskAction(targetUsecase, {
        isMultiObjective,
        evaluationResultsReady,
    });
    const url = taskUrlForUsecase(targetUsecase, {
        task_id: targetUsecase?._id,
        action,
    });
    if (!url) return null;
    return {
        url,
        usecaseId: targetUsecase?._id || null,
        state: undefined,
    };
};
